/**
 * EPC Service
 * API integration for EPC (Equipamentos de Proteção Coletiva) CRUD operations
 *
 * @version 2025.2.0
 * @description Serviço de integração com API de EPCs (entidade global)
 */
import apiClient from './apiClient';
import {
  Epc,
  EpcFilters,
  EpcFormData,
  EpcApiPayload,
  EpcListResponse,
  EpcDependencies,
  EpcAlerts,
  TipoDeRisco,
} from '../types/epc';

// Re-export types for convenience
export * from '../types/epc';

export interface EpcListParams extends EpcFilters {
  page?: number;
  limit?: number;
}

export type { UnresolvedTerm, AvailableOption, ImportPreviewResult } from '../types/import';
import type { ImportPreviewResult } from '../types/import';

export interface ImportMappings {
  tipo_de_risco?: Record<string, string>;
}

class EpcService {
  private baseUrl = '/epcs';

  /**
   * Convert form data to API payload
   */
  private formDataToApiPayload(formData: EpcFormData): EpcApiPayload {
    return {
      nome: formData.nome,
      tipoDeRisco: formData.tipoDeRisco,
      risco: formData.risco,
      eSocial: formData.eSocial || undefined,
      efficiency: formData.efficiency || undefined,
      brandModel: formData.brandModel || undefined,
      validade: formData.validade,
      revisao: formData.revisao || undefined,
      atenuacao: formData.atenuacao || undefined,
      proporcional: formData.proporcional || undefined,
      metodo: formData.metodo || undefined,
      custo: formData.custo || undefined,
      quantidade: formData.quantidade || undefined,
      unidade: formData.unidade || undefined,
      observacao: formData.observacao || undefined,
    };
  }

  /**
   * Convert API response to form data
   */
  apiResponseToFormData(apiData: Epc): EpcFormData {
    return {
      nome: apiData.nome,
      tipoDeRisco: apiData.tipoDeRisco,
      risco: apiData.risco,
      eSocial: apiData.eSocial || undefined,
      efficiency: apiData.efficiency || undefined,
      brandModel: apiData.brandModel || undefined,
      validade: apiData.validade,
      revisao: apiData.revisao || undefined,
      atenuacao: apiData.atenuacao || undefined,
      proporcional: apiData.proporcional || undefined,
      metodo: apiData.metodo || undefined,
      custo: apiData.custo || undefined,
      quantidade: apiData.quantidade || undefined,
      unidade: apiData.unidade || undefined,
      observacao: apiData.observacao || undefined,
    };
  }

  /**
   * Get paginated list of EPCs
   */
  async getEpcs(params: EpcListParams = {}): Promise<EpcListResponse> {
    const { page = 1, limit = 20, ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.tipoDeRisco) queryParams.append('tipoDeRisco', filters.tipoDeRisco);
    if (filters.expired !== undefined) queryParams.append('expired', String(filters.expired));
    if (filters.expiringSoon !== undefined) queryParams.append('expiringSoon', String(filters.expiringSoon));
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);

    // Backend returns: { data: { epcs, total, page, limit, totalPages } }
    const backendData = response.data.data || response.data;

    return {
      epcs: backendData.epcs ?? [],
      total: backendData.total ?? 0,
      page: backendData.page || page,
      limit: backendData.limit || limit,
      totalPages: backendData.totalPages || Math.ceil((backendData.total ?? 0) / limit),
    };
  }

  /**
   * Get EPC by ID
   */
  async getEpcById(id: string | number): Promise<Epc> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Create new EPC
   */
  async createEpc(data: EpcFormData): Promise<Epc> {
    const payload = this.formDataToApiPayload(data);
    const response = await apiClient.post(this.baseUrl, payload);
    return response.data.data || response.data;
  }

  /**
   * Update EPC by ID
   */
  async updateEpc(id: string | number, data: Partial<EpcFormData>): Promise<Epc> {
    const payload = this.formDataToApiPayload(data as EpcFormData);
    const response = await apiClient.put(`${this.baseUrl}/${id}`, payload);
    return response.data.data || response.data;
  }

  /**
   * Delete EPC by ID (soft delete)
   */
  async deleteEpc(id: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Get EPCs with expired validity
   */
  async getExpired(): Promise<Epc[]> {
    const response = await apiClient.get(`${this.baseUrl}/expired`);
    return response.data.data || response.data;
  }

  /**
   * Get EPCs with validity expiring soon
   */
  async getExpiringSoon(days: number = 30): Promise<Epc[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('days', days.toString());

    const response = await apiClient.get(`${this.baseUrl}/expiring-soon?${queryParams.toString()}`);
    return response.data.data || response.data;
  }

  /**
   * Get validity alerts (expired and expiring soon)
   */
  async getAlerts(days: number = 30): Promise<EpcAlerts> {
    const queryParams = new URLSearchParams();
    queryParams.append('days', days.toString());

    const response = await apiClient.get(`${this.baseUrl}/alerts?${queryParams.toString()}`);
    return response.data.data || response.data;
  }

  /**
   * Check dependencies before deletion
   */
  async checkDependencies(id: string | number): Promise<EpcDependencies> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/dependencies`);
    return response.data.data || response.data;
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data.data || response.data;
  }

  /**
   * Download import template
   */
  async downloadTemplate(): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/template`, { responseType: 'blob' });
    return response as unknown as Blob;
  }

  /**
   * Preview import EPCs from Excel (returns unresolved terms for mapping)
   */
  async previewImport(file: File): Promise<ImportPreviewResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${this.baseUrl}/import/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data ?? response.data;
  }

  /**
   * Import EPCs from Excel
   */
  async importEpcs(file: File, options?: { mappings?: ImportMappings }): Promise<{ imported: number; skipped?: number; errors: Array<{ row: number; error: string }> }> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.mappings) formData.append('mappings', JSON.stringify(options.mappings));
    const response = await apiClient.post(`${this.baseUrl}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data ?? response.data;
  }

  /**
   * Export EPCs to Excel
   */
  async exportEpcs(): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/export`, { responseType: 'blob' });
    return response as unknown as Blob;
  }

  /**
   * Get TipoDeRisco options for select dropdowns
   */
  getTipoDeRiscoOptions(): Array<{ value: TipoDeRisco; label: string }> {
    return [
      { value: TipoDeRisco.FISICO, label: 'Físico' },
      { value: TipoDeRisco.QUIMICO, label: 'Químico' },
      { value: TipoDeRisco.BIOLOGICO, label: 'Biológico' },
      { value: TipoDeRisco.ERGONOMICO, label: 'Ergonômico' },
      { value: TipoDeRisco.ACIDENTE, label: 'Acidente' },
    ];
  }

  /**
   * Get Proporcional options for select dropdowns
   */
  getProporcionalOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'Sim', label: 'Sim - Redução igual para todos' },
      { value: 'Não', label: 'Não - Redução variável' },
    ];
  }
}

// Export singleton instance
export const epcService = new EpcService();
export default epcService;
